import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  FaBrain, 
  FaCloud, 
  FaRobot, 
  FaChartLine, 
  FaShieldAlt, 
  FaCogs,
  FaArrowRight,
  FaCheckCircle
} from 'react-icons/fa';

const Home = () => {
  const services = [
    {
      icon: <FaBrain />,
      title: "Machine Learning Solutions",
      description: "Custom ML models and AI algorithms tailored to your business needs"
    },
    {
      icon: <FaCloud />,
      title: "Cloud Infrastructure",
      description: "Scalable cloud solutions with AWS, Azure, and Google Cloud expertise"
    },
    {
      icon: <FaRobot />,
      title: "AI Automation",
      description: "Intelligent automation systems to streamline your operations"
    },
    {
      icon: <FaChartLine />,
      title: "Data Analytics",
      description: "Advanced analytics and business intelligence solutions"
    },
    {
      icon: <FaShieldAlt />,
      title: "AI Security",
      description: "Cybersecurity solutions powered by artificial intelligence"
    },
    {
      icon: <FaCogs />,
      title: "System Integration",
      description: "Seamless integration of AI systems with existing infrastructure"
    }
  ];

  const features = [
    "Custom AI model development",
    "Cloud-native architecture design",
    "Real-time data processing",
    "Scalable infrastructure solutions",
    "24/7 monitoring and support",
    "Compliance and security focused"
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="hero-section text-white">
        <Container>
          <Row className="align-items-center min-vh-75">
            <Col lg={8} className="mx-auto text-center hero-content">
              <h1 className="display-4 fw-bold mb-4">
                Advanced AI Solutions for 
                <span className="text-warning"> Modern Businesses</span>
              </h1>
              <p className="lead mb-5 fs-5">
                Transform your business with cutting-edge artificial intelligence, 
                machine learning, and cloud infrastructure solutions. 
                We help companies leverage AI to drive innovation and growth.
              </p>
              <div className="d-grid gap-3 d-sm-flex justify-content-sm-center">
                <Button 
                  as={Link} 
                  to="/services" 
                  variant="light" 
                  size="lg" 
                  className="px-4 py-3 fw-semibold mb-2 mb-sm-0"
                >
                  Explore Services
                  <FaArrowRight className="ms-2" />
                </Button>
                <Button 
                  as={Link} 
                  to="/contact" 
                  variant="outline-light" 
                  size="lg" 
                  className="px-4 py-3 fw-semibold"
                >
                  Get Started
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Services Overview */}
      <section className="section-padding bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col lg={8} className="mx-auto">
              <h2 className="section-title">Our Services</h2>
              <p className="section-subtitle">
                Comprehensive AI and cloud solutions to accelerate your digital transformation
              </p>
            </Col>
          </Row>
          <Row>
            {services.map((service, index) => (
              <Col lg={4} md={6} key={index} className="mb-4">
                <Card className="h-100 service-card">
                  <div className="service-icon">
                    {service.icon}
                  </div>
                  <Card.Body className="d-flex flex-column">
                    <Card.Title className="fw-bold mb-3">{service.title}</Card.Title>
                    <Card.Text className="text-muted flex-grow-1">
                      {service.description}
                    </Card.Text>
                    <Button 
                      as={Link} 
                      to="/services" 
                      variant="outline-primary" 
                      className="mt-auto"
                    >
                      Learn More
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-5 mb-lg-0">
              <h2 className="section-title">Why Choose Stratis AI?</h2>
              <p className="section-subtitle">
                We combine deep technical expertise with business acumen to deliver 
                AI solutions that drive real results.
              </p>
              <div className="row">
                {features.map((feature, index) => (
                  <Col sm={6} key={index} className="mb-3">
                    <div className="d-flex align-items-center">
                      <FaCheckCircle className="text-primary me-2" />
                      <span>{feature}</span>
                    </div>
                  </Col>
                ))}
              </div>
              <Button 
                as={Link} 
                to="/about" 
                variant="primary" 
                size="lg" 
                className="mt-4"
              >
                Learn More About Us
              </Button>
            </Col>
            <Col lg={6}>
              <div className="bg-primary bg-opacity-10 p-5 rounded-3">
                <h3 className="fw-bold mb-4">Our Approach</h3>
                <div className="text-left">
                  <p className="mb-3">
                    We focus on delivering tailored AI solutions that solve real business problems. 
                    Our methodology combines cutting-edge technology with practical implementation.
                  </p>
                  <p className="mb-0">
                    From initial consultation to deployment and beyond, we work closely with our 
                    clients to ensure successful outcomes and sustainable growth.
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary text-white">
        <Container>
          <Row className="text-center">
            <Col lg={8} className="mx-auto">
              <h2 className="section-title text-white">Ready to Transform Your Business?</h2>
              <p className="section-subtitle text-white-50">
                Let's discuss how AI can drive innovation and growth for your organization. 
                Get in touch with our experts today.
              </p>
              <div className="d-grid gap-3 d-sm-flex justify-content-sm-center">
                <Button 
                  as={Link} 
                  to="/contact" 
                  variant="light" 
                  size="lg" 
                  className="px-4 py-3 fw-semibold mb-2 mb-sm-0"
                >
                  Schedule a Consultation
                </Button>
                <Button 
                  as={Link} 
                  to="/services" 
                  variant="outline-light" 
                  size="lg" 
                  className="px-4 py-3 fw-semibold"
                >
                  View Our Services
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Home;